import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { SimpleModal } from "./SimpleAudioModal";

interface AudioUsageTrackerProps {
  sessionUsed: number;
  lifetimeRemaining: number;
}

const formatSeconds = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
};

export const AudioUsageTracker: React.FC<AudioUsageTrackerProps> = ({
  sessionUsed,
  lifetimeRemaining,
}) => {
  const { t } = useTranslation("cvEditor");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const isLowTime = lifetimeRemaining < 600;

  return (
    <>
      <div
        className={`
          w-full bg-gray-800 text-white overflow-hidden transition-all duration-300 ease-in-out cursor-pointer 
          ${isExpanded ? "min-h-[32px] py-2" : "min-h-[6px] py-0"}
          ${isLowTime && !isExpanded ? "bg-orange-500" : ""}
        `}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        {/* Collapsed Progress Bar */}
        {!isExpanded && (
          <div className="w-full h-full bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`
                h-full transition-all duration-500
                ${
                  isLowTime
                    ? "bg-gradient-to-r from-orange-500 to-red-500 animate-pulse"
                    : "bg-gradient-to-r from-blue-500 to-green-500"
                }
              `}
              style={{
                width: `${Math.min(
                  (sessionUsed / (sessionUsed + lifetimeRemaining)) * 100,
                  100
                )}%`,
              }}
            />
          </div>
        )}

        {/* Expanded One-Line Display */}
        {isExpanded && (
          <div className="px-3 flex items-center justify-center text-xs gap-2 whitespace-nowrap">
            <span className="opacity-60">
              {t("interview.usage.sessionUsed", "Used")}:
            </span>
            <span className="font-semibold">{formatSeconds(sessionUsed)}</span>

            <span className="opacity-40">|</span>

            <span className="opacity-60">
              {t("interview.usage.lifetimeRemaining", "Remaining")}:
            </span>
            <span
              className={`font-semibold ${
                isLowTime ? "text-orange-400 animate-pulse" : ""
              }`}
            >
              {formatSeconds(lifetimeRemaining)}
            </span>

            <button
              onClick={() => setIsModalOpen(true)}
              className="ml-1 text-yellow-400 hover:text-yellow-300 transition-colors focus:outline-none"
              title={
                t("interview.usage.rechargeTooltip", "Recharge your time") ||
                "Recharge your time"
              }
              aria-label="Recharge audio time"
            >
              <AddCircleIcon
                sx={{ fontSize: 16, position: "relative", top: "-1px" }}
              />
            </button>
          </div>
        )}
      </div>

      <SimpleModal
        open={isModalOpen}
        title={t("interview.usage.rechargeTitle", "Recharge Audio Time")}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
